import { useState, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../Reveal";

const TimelineStep = ({ index, item, animKey, reduced }) => {
  const isFirst = index === 0;
  const dotSize = 44;
  const delay = reduced ? 0 : index * 70;
  return (
    <li
      data-testid={`timeline-step-${index}`}
      className="relative flex gap-5 md:gap-6"
      style={{
        animation: `${reduced ? "none" : "tl-in 420ms cubic-bezier(.22,.61,.36,1) both"}`,
        animationDelay: `${delay}ms`,
      }}
      key={`${animKey}-${index}`}
    >
      <div
        aria-hidden="true"
        style={{
          flex: "none",
          width: dotSize,
          height: dotSize,
          borderRadius: "50%",
          background: isFirst ? "var(--green-800)" : "transparent",
          border: isFirst
            ? "1px solid var(--green-800)"
            : "1px solid var(--gold-300)",
          color: isFirst ? "var(--white)" : "var(--gold-500)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--font-display)",
          fontSize: 18,
          lineHeight: 1,
          position: "relative",
          zIndex: 1,
        }}
      >
        {String(index + 1).padStart(2, "0")}
      </div>
      <div className="pb-10 md:pb-12 pt-1">
        <h3
          className="font-serif text-lg md:text-xl leading-snug"
          style={{ color: "var(--heading)" }}
        >
          {item.title}
        </h3>
        <p
          className="mt-2 text-[14px] md:text-[15px] leading-relaxed"
          style={{ color: "var(--fg-muted)", maxWidth: 480 }}
        >
          {item.body}
        </p>
      </div>
    </li>
  );
};

export const Services = ({ t, activeService, setActiveService }) => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const active =
    t.services.items.find((i) => i.key === activeService) ||
    t.services.items[0];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-36"
      style={{ background: "var(--paper)" }}
    >
      <style>{`
        @keyframes tl-in {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pane-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 md:mb-14 items-end">
          <div className="md:col-span-9">
            <Reveal>
              <span className="eyebrow mb-8">{t.services.label}</span>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="display-xl text-[34px] md:text-[48px] lg:text-[56px] mt-6">
                {t.services.title}
              </h2>
            </Reveal>
          </div>
        </div>

        <Reveal delay={180}>
          <div
            role="tablist"
            aria-label={t.services.label}
            data-testid="services-tablist"
            className="flex flex-wrap gap-3 mb-14 md:mb-20"
          >
            {t.services.items.map((it) => {
              const isActive = it.key === active.key;
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`pane-${it.key}`}
                  id={`tab-${it.key}`}
                  key={it.key}
                  data-testid={`tab-${it.key}`}
                  data-active={isActive}
                  onClick={() => setActiveService(it.key)}
                  style={{
                    borderRadius: "var(--radius-pill)",
                    padding: "13px 26px",
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    background: isActive ? "var(--green-800)" : "transparent",
                    color: isActive ? "var(--white)" : "var(--green-800)",
                    border: "1px solid var(--green-800)",
                    transition:
                      "background var(--dur) var(--ease), color var(--dur) var(--ease)",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = "var(--green-800)";
                      e.currentTarget.style.color = "var(--white)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "var(--green-800)";
                    }
                  }}
                >
                  {it.title}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div
          role="tabpanel"
          id={`pane-${active.key}`}
          aria-labelledby={`tab-${active.key}`}
          data-testid={`pane-${active.key}`}
          key={active.key}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20"
          style={{
            animation: reduced
              ? "none"
              : "pane-in 380ms cubic-bezier(.22,.61,.36,1) both",
          }}
        >
          <div className="lg:col-span-5">
            <h3
              className="display-xl text-[34px] md:text-[44px] lg:text-[52px]"
              data-testid={`pane-${active.key}-title`}
            >
              {active.title}
            </h3>
            <p
              className="mt-6 text-base md:text-lg leading-relaxed max-w-md"
              style={{ color: "var(--fg-muted)" }}
              data-testid={`pane-${active.key}-body`}
            >
              {active.body}
            </p>
            <a
              href="#contact"
              data-testid={`pane-${active.key}-cta`}
              className="btn btn-gold mt-10"
            >
              {t.services.ctaQuote}
              <ArrowUpRight size={16} />
            </a>

            <p
              className="mt-10 text-sm"
              style={{ color: "var(--fg-muted)", fontStyle: "italic" }}
              data-testid="process-note"
            >
              {t.services.processNote}
            </p>
          </div>

          <div className="lg:col-span-7">
            <ol
              data-testid={`timeline-${active.key}`}
              className="relative"
              style={{ listStyle: "none", padding: 0, margin: 0 }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: 22,
                  top: 22,
                  bottom: 22,
                  width: 1,
                  background: "var(--gold-300)",
                }}
              />
              {active.timeline.map((step, i) => (
                <TimelineStep
                  key={`${active.key}-${step.title}`}
                  index={i}
                  item={step}
                  animKey={active.key}
                  reduced={reduced}
                />
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};
